<template>
  <FocusExpandWrapper class="culture-number-input" :disabled="disabled">
    <input
      type="text"
      inputmode="decimal"
      :value="displayValue"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
      :disabled="disabled"
      :placeholder="placeholder"
      :class="inputClass"
      :id="id"
      :min="min"
      :max="max"
    />
  </FocusExpandWrapper>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useNumberFormatter } from '@/composables/useNumberFormatter';
import FocusExpandWrapper from '@/components/FocusExpandWrapper.vue';

const props = defineProps({
  modelValue: {
    type: Number,
    default: null
  },
  decimalPlaces: {
    type: Number,
    default: 2
  },
  displayDecimalPlaces: {
    type: Number,
    default: undefined
  },
  editDecimalPlaces: {
    type: Number,
    default: undefined
  },
  useGrouping: {
    type: Boolean,
    default: true
  },
  min: {
    type: Number,
    default: undefined
  },
  max: {
    type: Number,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ''
  },
  class: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'blur', 'input', 'change', 'focus']);

const { formatNumber, parseNumber } = useNumberFormatter();
const rawInput = ref('');
const isFocused = ref(false);

const effectiveDecimalPlaces = computed(() => {
  if (isFocused.value && props.editDecimalPlaces !== undefined) {
    return props.editDecimalPlaces;
  }
  if (!isFocused.value && props.displayDecimalPlaces !== undefined) {
    return props.displayDecimalPlaces;
  }
  return props.decimalPlaces;
});

const displayValue = computed(() => {
  if (rawInput.value !== '') {
    return rawInput.value;
  }
  
  if (props.modelValue === null || props.modelValue === undefined) {
    return '';
  }
  
  return formatNumber(props.modelValue, {
    decimalPlaces: effectiveDecimalPlaces.value,
    useGrouping: props.useGrouping
  });
});

watch(() => props.modelValue, () => {
  if (rawInput.value === '') {
    // no-op, computed handles formatting
  }
});

const handleInput = (event) => {
  const inputValue = event.target.value;
  rawInput.value = inputValue;
  
  if (!inputValue || inputValue.trim() === '') {
    const emptyValue = props.min !== undefined && props.min > 0 ? 0 : null;
    emit('update:modelValue', emptyValue);
  } else {
    try {
      let parsedValue = parseNumber(inputValue);
      
      if (props.min !== undefined && parsedValue < props.min) {
        parsedValue = props.min;
      }
      if (props.max !== undefined && parsedValue > props.max) {
        parsedValue = props.max;
      }
      
      emit('update:modelValue', parsedValue);
    } catch (e) {
      // ignore parsing errors during typing
    }
  }
  
  emit('input', event);
};

const handleFocus = (event) => {
  isFocused.value = true;
  
  // Select all text when focused so user can immediately start typing a new value
  // This allows replacing the existing value by just typing
  if (event.target?.select) {
    // Use setTimeout to ensure the selection happens after the focus event is fully processed
    setTimeout(() => {
      event.target.select();
    }, 0);
  }
  
  emit('focus', event);
};

const handleBlur = (event) => {
  isFocused.value = false;
  const inputValue = event.target.value;
  
  if (!inputValue || inputValue.trim() === '') {
    const emptyValue = props.min !== undefined && props.min > 0 ? 0 : null;
    emit('update:modelValue', emptyValue);
    rawInput.value = '';
  } else {
    let parsedValue = parseNumber(inputValue);
    
    if (props.min !== undefined && parsedValue < props.min) {
      parsedValue = props.min;
    }
    if (props.max !== undefined && parsedValue > props.max) {
      parsedValue = props.max;
    }
    
    emit('update:modelValue', parsedValue);
    rawInput.value = '';
  }
  
  emit('blur', event);
};

const handleChange = (event) => {
  emit('change', event);
};

const inputClass = computed(() => {
  return props.class || '';
});
</script>

<style scoped>
.culture-number-input {
  display: inline-flex;
  width: 100%;
}

.culture-number-input input {
  width: 100%;
  transition: padding 0.2s ease, background-color 0.2s ease;
}

.culture-number-input :deep(.focus-expand-wrapper.is-active) input {
  background-color: #f8fbff;
  padding-right: 1.75rem;
}
</style>

